import { Point } from './types'
import { MapElement } from '../../../types'

interface Line {
  start: Point
  end: Point
}

// Calculate the intersection point of two lines (extended infinitely)
const getLineIntersection = (
  line1Start: Point,
  line1End: Point,
  line2Start: Point,
  line2End: Point,
): Point | null => {
  const x1 = line1Start.x
  const y1 = line1Start.y
  const x2 = line1End.x
  const y2 = line1End.y
  const x3 = line2Start.x
  const y3 = line2Start.y
  const x4 = line2End.x
  const y4 = line2End.y

  const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4)

  // Lines are parallel
  if (Math.abs(denom) < 0.0001) return null

  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom

  return {
    x: x1 + t * (x2 - x1),
    y: y1 + t * (y2 - y1),
  }
}

// Check if two points are close enough to be considered connected
const arePointsConnected = (
  p1: Point,
  p2: Point,
  threshold: number = 10,
): boolean => {
  const dx = p1.x - p2.x
  const dy = p1.y - p2.y
  return Math.sqrt(dx * dx + dy * dy) <= threshold
}

// Find walls that connect at a point
export const findConnectedWalls = (
  elements: MapElement[],
  point: Point,
  currentWallId?: string,
): MapElement[] => {
  const walls = elements.filter(
    el =>
      el.type === 'wall' &&
      el.properties.endPosition &&
      el.id !== currentWallId,
  )

  return walls.filter(wall => {
    const start = wall.position
    const end = wall.properties.endPosition!

    return arePointsConnected(start, point) || arePointsConnected(end, point)
  })
}

// Calculate the miter joint points for connected walls
export const calculateMiterJoint = (
  wall1: MapElement,
  wall2: MapElement,
  connectionPoint: Point,
  strokeWidth: number = 2,
): { wall1Extended: Point; wall2Extended: Point } | null => {
  if (!wall1.properties.endPosition || !wall2.properties.endPosition)
    return null

  // Determine which end of each wall connects
  const wall1Start = wall1.position
  const wall1End = wall1.properties.endPosition
  const wall1ConnectsAtStart = arePointsConnected(wall1Start, connectionPoint)

  const wall2Start = wall2.position
  const wall2End = wall2.properties.endPosition
  const wall2ConnectsAtStart = arePointsConnected(wall2Start, connectionPoint)

  // Get the direction vectors for each wall
  const wall1Dir = wall1ConnectsAtStart
    ? { x: wall1Start.x - wall1End.x, y: wall1Start.y - wall1End.y }
    : { x: wall1End.x - wall1Start.x, y: wall1End.y - wall1Start.y }

  const wall2Dir = wall2ConnectsAtStart
    ? { x: wall2Start.x - wall2End.x, y: wall2Start.y - wall2End.y }
    : { x: wall2End.x - wall2Start.x, y: wall2End.y - wall2Start.y }

  // Normalize direction vectors
  const wall1Length = Math.sqrt(
    wall1Dir.x * wall1Dir.x + wall1Dir.y * wall1Dir.y,
  )
  const wall2Length = Math.sqrt(
    wall2Dir.x * wall2Dir.x + wall2Dir.y * wall2Dir.y,
  )

  if (wall1Length === 0 || wall2Length === 0) return null

  wall1Dir.x /= wall1Length
  wall1Dir.y /= wall1Length
  wall2Dir.x /= wall2Length
  wall2Dir.y /= wall2Length

  // Calculate angle between walls
  const dotProduct = wall1Dir.x * wall2Dir.x + wall1Dir.y * wall2Dir.y
  const angle = Math.acos(Math.max(-1, Math.min(1, dotProduct)))

  // If walls are nearly parallel or opposite, no need for special joining
  if (Math.abs(angle) < 0.1 || Math.abs(angle - Math.PI) < 0.1) {
    return null
  }

  // Calculate the miter length
  const miterLength = strokeWidth / Math.sin(angle / 2)

  // Prevent excessively long miters
  const maxMiterLength = strokeWidth * 3
  const actualMiterLength = Math.min(miterLength, maxMiterLength)

  // Calculate the bisector direction
  const bisectorX = wall1Dir.x + wall2Dir.x
  const bisectorY = wall1Dir.y + wall2Dir.y
  const bisectorLength = Math.sqrt(
    bisectorX * bisectorX + bisectorY * bisectorY,
  )

  if (bisectorLength === 0) return null

  // Extend the connection point along the bisector
  const extendedPoint = {
    x: connectionPoint.x + (bisectorX / bisectorLength) * actualMiterLength,
    y: connectionPoint.y + (bisectorY / bisectorLength) * actualMiterLength,
  }

  return {
    wall1Extended: extendedPoint,
    wall2Extended: extendedPoint,
  }
}

// Simple approach: extend wall ends slightly to cover gaps
const extendWallEnd = (
  wallStart: Point,
  wallEnd: Point,
  connectionPoint: Point,
  extensionAmount: number = 3,
): Point => {
  // Determine which end needs extending
  const isStartPoint = arePointsConnected(wallStart, connectionPoint)

  if (isStartPoint) {
    // Extend the start point
    const dx = wallStart.x - wallEnd.x
    const dy = wallStart.y - wallEnd.y
    const length = Math.sqrt(dx * dx + dy * dy)

    if (length === 0) return wallStart

    return {
      x: wallStart.x + (dx / length) * extensionAmount,
      y: wallStart.y + (dy / length) * extensionAmount,
    }
  } else {
    // Extend the end point
    const dx = wallEnd.x - wallStart.x
    const dy = wallEnd.y - wallStart.y
    const length = Math.sqrt(dx * dx + dy * dy)

    if (length === 0) return wallEnd

    return {
      x: wallEnd.x + (dx / length) * extensionAmount,
      y: wallEnd.y + (dy / length) * extensionAmount,
    }
  }
}

// Draw walls with proper corner joining
export const drawWallWithJoins = (
  ctx: CanvasRenderingContext2D,
  wall: MapElement,
  allElements: MapElement[],
  strokeWidth: number = 6,
) => {
  if (!wall.properties.endPosition) return

  const start = wall.position
  const end = wall.properties.endPosition
  const extensionAmount = Math.min(strokeWidth / 4, 0) // Much smaller extension, max 2px

  // Find connected walls at each end
  const connectedAtStart = findConnectedWalls(allElements, start, wall.id)
  const connectedAtEnd = findConnectedWalls(allElements, end, wall.id)

  let adjustedStart = start
  let adjustedEnd = end

  // Use end cap type from properties, default to 'square' for clean edges
  const endCapType = wall.properties.endCapType || 'square'

  // Only extend endpoints if using 'square' end cap type and walls are connected
  if (endCapType === 'square') {
    if (connectedAtStart.length > 0) {
      adjustedStart = extendWallEnd(start, end, start, extensionAmount)
    }

    if (connectedAtEnd.length > 0) {
      adjustedEnd = extendWallEnd(start, end, end, extensionAmount)
    }
  }

  // Set material-based styling
  const material = wall.properties.material || 'drywall'

  switch (material) {
    case 'brick':
      ctx.strokeStyle = wall.properties.color || '#8B4513'
      ctx.setLineDash([8, 4])
      break
    case 'concrete':
      ctx.strokeStyle = wall.properties.color || '#696969'
      ctx.setLineDash([12, 2, 4, 2])
      break
    case 'glass':
      ctx.strokeStyle = wall.properties.color || '#87CEEB'
      ctx.setLineDash([2, 6])
      break
    case 'metal':
      ctx.strokeStyle = wall.properties.color || '#C0C0C0'
      ctx.setLineDash([4, 4])
      break
    default: // drywall/wood frame
      ctx.strokeStyle = wall.properties.color || '#333333'
      ctx.setLineDash([])
  }

  // Draw the wall with adjusted endpoints
  ctx.beginPath()
  ctx.moveTo(adjustedStart.x, adjustedStart.y)
  ctx.lineTo(adjustedEnd.x, adjustedEnd.y)
  ctx.lineWidth = strokeWidth
  ctx.lineCap = endCapType as CanvasLineCap
  ctx.stroke()

  // Reset dash
  ctx.setLineDash([])
}
